/**
 * Schema.org structured data utilities for SEO enhancement
 *
 * Centralized JSON-LD schema generators for Catalyst Cannabis.
 * Used across route meta functions to generate structured data.
 */

// Map store values to location page slugs for seller @id references
const storeValueToSlug: Record<string, string> = {
  catalystantioch: 'antioch-dispensary',
  catalystbellflower: 'bellflower-dispensary',
  catalystbelmontshore: 'belmont-shore-dispensary',
  catalystcherryave: 'cherry-ave-long-beach-dispensary',
  catalystcostamesa: 'costa-mesa-dispensary',
  catalystdalycity: 'daly-city-dispensary',
  catalystdowntownlb: 'downtown-long-beach-dispensary',
  catalystelmonte: 'el-monte-dispensary',
  catalystfigueroa: 'figueroa-los-angeles-dispensary',
  catalystflorence: 'florence-los-angeles-dispensary',
  catalystgroverbeach: 'grover-beach-dispensary',
  catalysthawthorne: 'hawthorne-dispensary',
  catalysthemet: 'hemet-dispensary',
  catalystlancaster: 'lancaster-dispensary',
  catalystlynwood: 'lynwood-dispensary',
  catalystmarina: 'marina-dispensary',
  catalystmidcity: 'mid-city-los-angeles-dispensary',
  catalystmorenovalley: 'moreno-valley-dispensary',
  catalystnormandie: 'normandie-los-angeles-dispensary',
  catalystoc3: 'oc3-santa-ana-dispensary',
  catalystoxnard: 'oxnard-dispensary',
  catalystpalmdesert: 'palm-desert-dispensary',
  catalystpatterson: 'patterson-dispensary',
  catalystpomona: 'pomona-dispensary',
  catalystplanetoc: 'planet-catalyst-orange-county',
  catalystsanbernardino: 'san-bernardino-dispensary',
  catalystsandiego: 'san-diego-dispensary',
  catalystsantaana: 'santa-ana-dispensary',
  catalystsilverlake: 'silver-lake-dispensary',
  catalyststanton: 'stanton-dispensary',
  catalystvannuys: 'van-nuys-dispensary',
  catalystwatsonville: 'watsonville-dispensary',
  catalystretrorow: 'retro-row-long-beach-dispensary',
};

const BASE_URL = 'https://catalyst-cannabis.com';

/**
 * Get the location slug for a given store value
 */
export function getLocationSlug(store: string): string | undefined {
  return storeValueToSlug[store];
}

/**
 * Generate Product schema for cannabis products
 */
export function generateProductSchema(product: {
  name: string;
  slug: string;
  brand: string;
  category: string;
  id: string;
  price: number;
  salePrice?: number;
  image?: string;
  product_description?: string;
  thc?: string;
  size?: string;
  classification?: string;
}, store: string) {
  const productUrl = `${BASE_URL}/product/${product.slug}`;
  const imageUrl = product.image?.startsWith('http') ? product.image : `${BASE_URL}${product.image}`;

  const locationSlug = storeValueToSlug[store];
  const seller = locationSlug
    ? { '@type': 'LocalBusiness', '@id': `${BASE_URL}/dispensary-locations/${locationSlug}` }
    : { '@type': 'Organization', name: 'Catalyst Cannabis' };

  const offers = [];

  if (product.price > 0) {
    offers.push({
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'USD',
      price: product.price.toFixed(2),
      availability: 'https://schema.org/InStock',
      seller,
      validFrom: new Date().toISOString().split('T')[0],
    });
  }

  if (product.salePrice && product.salePrice < product.price) {
    offers.push({
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'USD',
      price: product.salePrice.toFixed(2),
      availability: 'https://schema.org/InStock',
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      seller,
      validFrom: new Date().toISOString().split('T')[0],
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.product_description || `${product.name} by ${product.brand}`,
    brand: { '@type': 'Brand', name: product.brand },
    category: product.category,
    sku: product.id,
    image: imageUrl,
    url: productUrl,
    offers,
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'THC Content', value: product.thc },
      { '@type': 'PropertyValue', name: 'Size', value: product.size },
      { '@type': 'PropertyValue', name: 'Classification', value: product.classification },
    ],
    manufacturer: { '@type': 'Organization', name: product.brand },
  };
}

/**
 * Generate Store/LocalBusiness schema for dispensary locations
 */
export function generateLocationSchema(locationData: {
  name: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
  };
  phone?: string;
  url: string;
  description?: string;
  /**
   * One entry per distinct opening pattern. A single entry covering all seven
   * days is the common case; stores with different weekend hours need several,
   * which is why this is a list rather than one opens/closes pair.
   */
  openingHoursSpecification?: Array<{
    dayOfWeek: string | string[];
    opens: string;
    closes: string;
  }>;
  geo?: {
    latitude: number;
    longitude: number;
  };
  topProducts?: Array<{
    name: string;
    slug: string;
    brand: string;
    category: string;
    price: number;
    salePrice?: number;
    image?: string;
  }>;
  areaServed?: string[];
  hasMap?: string;
  image?: string;
  email?: string;
  priceRange?: string;
  paymentAccepted?: string[];
  currenciesAccepted?: string;
  /**
   * Omit entirely when the store has no review data. A default would be
   * invented review data in structured data, which is a guidelines violation
   * rather than a cosmetic fallback — and it must also be visible on the page.
   */
  aggregateRating?: { ratingValue: number; reviewCount: number };
}) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': locationData.url,
    name: locationData.name,
    description: locationData.description ||
      `${locationData.name} - Cannabis dispensary in ${locationData.address.addressLocality}, California`,
    url: locationData.url,
    telephone: locationData.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: locationData.address.streetAddress,
      addressLocality: locationData.address.addressLocality,
      addressRegion: locationData.address.addressRegion,
      postalCode: locationData.address.postalCode,
      addressCountry: 'US',
    },
    parentOrganization: {
      '@type': 'Organization',
      name: 'Catalyst Cannabis',
      url: BASE_URL,
    },
    brand: { '@type': 'Brand', name: 'Catalyst Cannabis' },
    priceRange: '$$',
    paymentAccepted: ['Cash', 'Debit Card'],
    currenciesAccepted: 'USD',
  };

  if (locationData.geo) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: locationData.geo.latitude,
      longitude: locationData.geo.longitude,
    };
  }

  if (locationData.openingHoursSpecification?.length) {
    schema.openingHoursSpecification = locationData.openingHoursSpecification.map((spec) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: spec.dayOfWeek,
      opens: spec.opens,
      closes: spec.closes,
    }));
  }

  if (locationData.image) schema.image = locationData.image;
  if (locationData.email) schema.email = locationData.email;
  if (locationData.priceRange) schema.priceRange = locationData.priceRange;
  if (locationData.paymentAccepted) schema.paymentAccepted = locationData.paymentAccepted;
  if (locationData.currenciesAccepted) schema.currenciesAccepted = locationData.currenciesAccepted;

  if (locationData.aggregateRating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: locationData.aggregateRating.ratingValue,
      reviewCount: locationData.aggregateRating.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  schema.parentOrganization = {
    '@type': 'Organization',
    name: 'Catalyst Cannabis',
    url: BASE_URL,
  };

  if (locationData.hasMap) {
    schema.hasMap = locationData.hasMap;
  }

  if (locationData.areaServed && locationData.areaServed.length > 0) {
    schema.areaServed = locationData.areaServed.map(area => ({
      '@type': 'City',
      name: area,
    }));
  }

  if (locationData.topProducts && locationData.topProducts.length > 0) {
    schema.itemOffered = locationData.topProducts.map(product => {
      const productUrl = `${BASE_URL}/product/${product.slug}`;
      const imageUrl = product.image?.startsWith('http') ? product.image : `${BASE_URL}${product.image}`;

      return {
        '@type': 'Product',
        name: product.name,
        url: productUrl,
        ...(product.image && { image: imageUrl }),
        brand: { '@type': 'Brand', name: product.brand },
        category: product.category,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: (product.salePrice && product.salePrice < product.price
            ? product.salePrice : product.price).toFixed(2),
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'LocalBusiness', '@id': locationData.url },
        },
      };
    });
  }

  return schema;
}

/**
 * Generate Organization schema for Catalyst Cannabis
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Catalyst Cannabis',
    alternateName: 'Catalyst Cannabis Dispensaries',
    url: BASE_URL,
    logo: `${BASE_URL}/assets/icons/android-chrome-192x192.png`,
    description: 'California cannabis dispensary chain offering premium flower, vapes, edibles, concentrates, and more across multiple locations.',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'English',
    },
    sameAs: [
      'https://www.instagram.com/catalystcannabis/',
      'https://www.facebook.com/catalystcannaco/',
      'https://x.com/Catalyst_WFTP',
    ],
  };
}

/**
 * Generate BreadcrumbList schema
 */
export function generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/**
 * Generate FAQPage schema
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/<[^>]*>/g, ''),
      },
    })),
  };
}

/**
 * Generate ItemList schema for listing pages (categories, deals, blog index)
 */
export function generateItemListSchema(
  items: Array<{ name: string; url: string; image?: string; description?: string }>,
  listName: string,
  listUrl: string,
) {
  if (!listName || !listUrl) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    url: listUrl,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 20).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
      ...(item.image && { image: item.image }),
      ...(item.description && { description: item.description }),
    })),
  };
}

/**
 * Generate CollectionPage schema for category and listing pages
 */
export function generateCollectionPageSchema(name: string, description: string, url: string) {
  if (!name || !url) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url,
    isPartOf: { '@type': 'WebSite', name: 'Catalyst Cannabis', url: BASE_URL },
    provider: { '@type': 'Organization', name: 'Catalyst Cannabis', url: BASE_URL },
  };
}

/**
 * Generate BlogPosting schema for individual blog posts
 */
/**
 * The author a post claims, if anyone has set one.
 *
 * Lives in the post's `seo` JSONB bag (budflow-skunk migration 163) because
 * `website_blog_posts` has no author column — there has never been a per-post
 * byline to bind to. Page Settings → Structured Data has offered an "Author
 * name" field all along and nothing read it: Catalyst's Costa Mesa post has
 * carried `"Catalyst Editorial"` since it was migrated, while the live page has
 * been saying `Catalyst Cannabis` the whole time. Typed, stored, dropped.
 */
export function authorFromSeoBag(seo: unknown): string | null {
  if (!seo || typeof seo !== 'object') return null;
  const sd = (seo as { structuredData?: { fields?: Record<string, unknown> } }).structuredData;
  const raw = sd?.fields?.author;
  const name = typeof raw === 'string' ? raw.trim() : '';
  return name || null;
}

export function generateBlogPostSchema(post: {
  title: string;
  date: string;
  content: string;
  description?: string;
  image?: string;
  slug: string;
  /** From `authorFromSeoBag`. Omitted or empty falls back to the brand. */
  authorName?: string | null;
}) {
  const postUrl = `${BASE_URL}/blog/${post.slug}`;
  const imageUrl = post.image?.startsWith('http')
    ? post.image
    : post.image
      ? `https://d14jfwlo68bhyr.cloudfront.net/catalyst/blog${post.image}`
      : `${BASE_URL}/images/default.webp`;

  const plainText = post.content.replace(/<[^>]*>/g, '');
  const wordCount = plainText.split(/\s+/).filter(word => word.length > 0).length;
  const description = post.description || plainText.substring(0, 160).trim() + (plainText.length > 160 ? '...' : '');

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description,
    image: imageUrl,
    datePublished: post.date,
    dateModified: post.date,
    wordCount,
    url: postUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
    isPartOf: { '@type': 'Blog', name: 'Catalyst Cannabis Blog', url: `${BASE_URL}/blog` },
    /**
     * A named author when the post has one, the brand otherwise.
     *
     * `Person` rather than `Organization` for a byline — schema.org accepts
     * either, and Google's article guidance asks for the name of the person who
     * wrote it where there is one. No `url`: we have nothing authoritative to
     * point at, and inventing an author page would be worse than omitting it.
     * The brand fallback is not a placeholder — for a dispensary blog, corporate
     * authorship is the honest answer and has been shipping for years.
     */
    author: post.authorName
      ? { '@type': 'Person', name: post.authorName }
      : { '@type': 'Organization', name: 'Catalyst Cannabis', url: BASE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'Catalyst Cannabis',
      url: BASE_URL,
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/assets/icons/android-chrome-192x192.png` },
    },
  };
}

/**
 * Generate ItemList schema for blog listing page
 */
export function generateBlogListSchema(posts: Array<{ title: string; slug: string; image?: string }>) {
  if (!posts || posts.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Catalyst Cannabis Blog',
    url: `${BASE_URL}/blog`,
    numberOfItems: posts.length,
    itemListElement: posts.slice(0, 20).map((post, index) => {
      const imageUrl = post.image?.startsWith('http')
        ? post.image
        : post.image
          ? `https://d14jfwlo68bhyr.cloudfront.net/catalyst/blog${post.image}`
          : undefined;

      return {
        '@type': 'ListItem',
        position: index + 1,
        name: post.title,
        url: `${BASE_URL}/blog/${post.slug}`,
        ...(imageUrl && { image: imageUrl }),
      };
    }),
  };
}

/**
 * Helper to safely stringify schema for JSON-LD injection
 */
export function safeStringifySchema(schema: unknown): string {
  try {
    return JSON.stringify(schema, null, 0);
  } catch {
    return '{}';
  }
}
